import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { OrganisationSettingsService } from '@/api';
import type { CreateTableConfigRequest, UpdateTableConfigRequest, TableConfig } from '@/types/organisation';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { errorToastText } from '@/lib/translateBackendError';

export function useTableConfigs(entityType?: string) {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ['table-configs', orgId, entityType],
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      
      const response = await OrganisationSettingsService.getApiV1TableConfigs();
      
      // Filter by entity type if provided
      if (entityType && response) {
        return response.filter((config: TableConfig) => config.entityType === entityType);
      }
      
      return response;
    },
    enabled: !!orgId,
  });
}

export function useTableConfig(entityType: string, childcareId?: string) {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ['table-config', orgId, entityType, childcareId],
    queryFn: async () => {
      if (!orgId) throw new Error('No organisation ID available');
      
      const response = await OrganisationSettingsService.getApiV1TableConfigs();
      
      // Find config for specific entity type and optionally childcare
      if (response) {
        return response.find((config: TableConfig) => 
          config.entityType === entityType && 
          (childcareId ? config.childcareId === childcareId : !config.childcareId)
        ) || null;
      }
      
      return null;
    },
    enabled: !!orgId && !!entityType,
  });
}

export function useCreateTableConfig() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: async (data: CreateTableConfigRequest) => {
      if (!orgId) throw new Error('No organisation ID available');
      return OrganisationSettingsService.postApiV1TableConfigs(data);
    },
    onSuccess: (_, variables) => {
      toast.success(t`Table configuration created successfully`);
      queryClient.invalidateQueries({ queryKey: ['table-configs', orgId] });
      queryClient.invalidateQueries({ queryKey: ['table-config', orgId, variables.entityType] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
    },
    onError: (error) => {
      console.error('Failed to create table config:', error);
      toast.error(
        errorToastText(
          error,
          i18n,
          msg({
            id: 'settings.table.errors.createFailed',
            message: 'Failed to create table configuration',
          })
        )
      );
    },
  });
}

export function useUpdateTableConfig() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  const { t, i18n } = useLingui();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTableConfigRequest }) => {
      if (!orgId) throw new Error('No organisation ID available');
      return OrganisationSettingsService.putApiV1TableConfigs(id, data);
    },
    onSuccess: (updatedConfig) => {
      toast.success(t`Table configuration updated successfully`);
      queryClient.invalidateQueries({ queryKey: ['table-configs', orgId] });
      if (updatedConfig.entityType) {
        queryClient.invalidateQueries({ queryKey: ['table-config', orgId, updatedConfig.entityType] });
      }
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
    },
    onError: (error) => {
      console.error('Failed to update table config:', error);
      toast.error(
        errorToastText(
          error,
          i18n,
          msg({
            id: 'settings.table.errors.updateFailed',
            message: 'Failed to update table configuration',
          })
        )
      );
    },
  });
}

export function useDeleteTableConfig() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async ({ id, entityType }: { id: string; entityType: string }) => {
      if (!orgId) throw new Error('No organisation ID available');
      return OrganisationSettingsService.deleteApiV1TableConfigs(id, entityType);
    },
    onSuccess: () => {
      toast.success(t`Table configuration deleted successfully`);
      queryClient.invalidateQueries({ queryKey: ['table-configs', orgId] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'effective'] });
    },
    onError: () => {
      toast.error(t`Failed to delete table configuration`);
    },
  });
}